import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import EmailTestModeToggle from '../components/common/EmailTestModeToggle';
import {
  ReceiptPercentIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { collection, query, where, getDocs, doc, getDoc, documentId } from 'firebase/firestore';
import { POD_COLLECTION } from '../config/collections';
import { db } from '../lib/firebase';
import { createInvoiceWithAttachments } from '../services/billingService';
import { sendInvoiceLinkEmail, sendGeneralEmail } from '../services/emailService';
import { calculateIndexedUnitPrice, IndexedPriceResult } from '../services/calculateIndexedPrice';

interface POD {
  id: string;
  address: string;
  partnerId?: string;
  postalCode?: string;
  city?: string;
  street?: string;
  measurementType?: string;
  annualConsumption?: string;
  invoiceGroupId?: string;
  multiplier?: number;
  billable?: boolean;
}
                                                        
interface PricingData {
  year: number;
  validFrom: string;
  validTo: string;
  price: string;
  currency: string;
  consumption_kwh?: string;
  pod_id?: string;
  status: 'active' | 'future' | 'expired';
}

interface Customer {
  id: string;
  name?: string;
  companyName?: string;
  email?: string;
  billingEmail?: string;
  additionalBillingEmails?: string[];
  financialContact?: {
    name?: string;
    email?: string;
    phone?: string;
  };
  taxNumber?: string;
  address?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  status?: 'lead' | 'prospect' | 'quoted' | 'contract' | 'active' | 'inactive' | 'lost';
  pod?: POD;
  additionalPods?: POD[];
  currentPricing?: PricingData;
  offerPrice?: { price: string; currency: string };
  contractPeriod?: {
    price: string;
    priceEUR?: string;
    indexedPrice?: boolean;
    spread?: string;
    priceUnit?: 'HUF/kWh' | 'EUR/MWh';
    paymentCurrency?: 'HUF' | 'EUR';
  };
  bulkInvoicingSettings?: {
    invoicingMode: 'per_pod' | 'consolidated' | 'all_pods_single_invoice';
  };
  invoiceGroups?: Array<{
    id: string;
    name: string;
    podIds: string[];
  }>;
}

interface BillingItem {
  id: string;
  name: string;
  description: string;
  unit: string;
  unitPrice: number;
  taxRate: number;
  category: string;
  sortOrder: number;
}

// Helper to collect all billing emails from a customer
const getAllCustomerEmails = (customer: Customer): string[] => {
  const emails: string[] = [];
  if (customer.email) emails.push(customer.email);
  if (customer.billingEmail) emails.push(customer.billingEmail);
  if (customer.financialContact?.email) emails.push(customer.financialContact.email);
  if (customer.additionalBillingEmails && customer.additionalBillingEmails.length > 0) {
    emails.push(...customer.additionalBillingEmails);
  }
  return emails.filter((e, i) => emails.indexOf(e) === i); // deduplicate
};

interface InvoiceLineItem {
  id: string;
  billingItemId: string;
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  taxRate: number;
  comment?: string;
  sortOrder?: number;
  indexedPriceInfo?: {
    isIndexed: boolean;
    hupxAvgPriceEUR: number;
    eurHufRate?: number;
    spread?: number;
    totalConsumption: number;
    matchedIntervals: number;
    currency: string;
  };
}

interface InvoiceDraft {
  id: string;
  customerId: string;
  customerName: string;
  podId?: string;
  podAddress?: string;
  allPods?: Array<{ id: string; address?: string; selected?: boolean }>;
  items: InvoiceLineItem[];
  selected: boolean;
  invoicingMode?: 'per_pod' | 'consolidated' | 'all_pods_single_invoice';
  currency?: string;
  performanceDate?: string;
  paymentDeadline?: number;
  notes?: string;
  invoiceType?: 'draft' | 'proforma' | 'final';
  attachmentCode?: string;
  attachmentUrl?: string;
  excelUrl?: string;
}

/**
 * Parse customer address from any format (string, object, or individual fields)
 * Handles: "4700 Mátészalka, Babits M. út 14" or {city, postalCode, street} or customer.city + customer.street
 */
function parseCustomerAddress(customer: any): { city: string; postalCode: string; street: string } {
  const result = { city: '', postalCode: '', street: '' };
  if (!customer) return result;

  // Format 1: object with fields
  if (typeof customer.address === 'object' && customer.address) {
    result.city = customer.address.city || '';
    result.postalCode = customer.address.postalCode || '';
    result.street = customer.address.street || '';
    if (result.city && result.postalCode && result.street) return result;
  }

  // Format 2: individual fields
  if (customer.city) result.city = customer.city;
  if (customer.postalCode) result.postalCode = customer.postalCode;
  if (customer.street) result.street = customer.street;
  if (result.city && result.postalCode && result.street) return result;

  // Format 3: string like "4700 Mátészalka, Babits M. út 14"
  if (typeof customer.address === 'string' && customer.address) {
    const match = customer.address.match(/^(\d{4})\s+(.+?),\s*(.+)$/);
    if (match) {
      result.postalCode = match[1];
      result.city = match[2];
      result.street = match[3];
    } else {
      // Fallback: just use as street if no pattern match
      result.street = customer.address;
    }
  }

  return result;
}

const BulkInvoiceManager = () => {
  const { user } = useAuth();
  
  // Helper: try to get customer price from multiple sources
  const getCustomerPrice = (customer: Customer | undefined): number | null => {
    if (!customer) return null;
    // 1. currentPricing.price
    if (customer.currentPricing?.price) {
      const price = parseFloat(customer.currentPricing.price);
      if (!Number.isNaN(price) && price > 0) return price;
    }
    // 2. offerPrice.price
    if (customer.offerPrice?.price) {
      const price = parseFloat(customer.offerPrice.price);
      if (!Number.isNaN(price) && price > 0) return price;
    }
    // 3. contractPeriod.price
    if (customer.contractPeriod?.price) {
      const price = parseFloat(customer.contractPeriod.price);
      if (!Number.isNaN(price) && price > 0) return price;
    }
    return null;
  };
  
  const [step, setStep] = useState<'customers' | 'items' | 'review'>('customers');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [invoiceDrafts, setInvoiceDrafts] = useState<InvoiceDraft[]>([]);
  const invoiceDraftsRef = useRef(invoiceDrafts);
  invoiceDraftsRef.current = invoiceDrafts;
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<{
    isOpen: boolean;
    total: number;
    current: number;
    steps: Array<{
      customerName: string;
      status: 'pending' | 'processing' | 'success' | 'error';
      message?: string;
      invoiceNumber?: string;
      attachmentCode?: string;
      emailSent?: boolean;
      emailError?: string;
    }>;
  }>({ isOpen: false, total: 0, current: 0, steps: [] });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
  }>({ isOpen: false, title: '', message: '', onConfirm: null });
  const [hasSettings, setHasSettings] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [consumptionCache, setConsumptionCache] = useState<Map<string, number>>(new Map());
  const [billingPeriod, setBillingPeriod] = useState<{ year: number; month: number }>({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  const [invoicingMode, setInvoicingMode] = useState<'all_pods_single_invoice' | 'per_pod'>('all_pods_single_invoice');
  const [includeReverseFeed, setIncludeReverseFeed] = useState(false); // Visszatáplálást számlázunk
  const [skipZeroNetConsumption, setSkipZeroNetConsumption] = useState(false); // Nulla nettó fogyasztás kihagyása
  const [dataSource, setDataSource] = useState<'mscons' | 'measurements'>('measurements'); // Adatforrás
  const [podDateRanges, setPodDateRanges] = useState<Map<string, { start: Date; end: Date }>>(new Map());

  useEffect(() => {
    if (user?.organizationId) {
      loadCustomers();
      loadBillingItems();
    }
  }, [user?.organizationId]);


  const loadCustomers = async () => {
    try {
      setLoading(true);
      
      // Load CRM customers
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customersData = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Customer[];
      
      // Load all PODs from energy_pods collection (pipeline data)
      const energyPodsSnapshot = await getDocs(collection(db, POD_COLLECTION));
      const energyPods = energyPodsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as any));
      
      console.log(`📊 Loaded ${customersData.length} CRM customers and ${energyPods.length} energy PODs`);
      
      // Create a map of POD ID to customer for existing CRM customers
      const podToCustomerMap = new Map<string, Customer>();
      customersData.forEach(customer => {
        if (customer.pod?.id) {
          podToCustomerMap.set(customer.pod.id, customer);
        }
        // Ellenőrizzük, hogy az additionalPods valóban tömb-e
        if (customer.additionalPods && Array.isArray(customer.additionalPods)) {
          customer.additionalPods.forEach(pod => {
            if (pod.id) {
              podToCustomerMap.set(pod.id, customer);
            }
          });
        } else if (customer.additionalPods) {
          // Ha nem tömb, logoljuk a hibát
          console.warn(`⚠️ Customer ${customer.companyName || customer.id} has invalid additionalPods (not an array):`, typeof customer.additionalPods);
        }
      });
      
      // Only use real CRM customers - no virtual customers for pipeline PODs
      console.log(`✅ Loaded ${customersData.length} CRM customers (pipeline PODs excluded)`);
      
      // Build POD date range map
      const drMap = new Map<string, { start: Date; end: Date }>();
      energyPods.forEach((pod: any) => {
        const start = pod.date_range?.start?.toDate ? pod.date_range.start.toDate() : null;
        const end = pod.date_range?.end?.toDate ? pod.date_range.end.toDate() : null;
        if (start && end) drMap.set(pod.id, { start, end });
      });
      setPodDateRanges(drMap);
      
      setCustomers(customersData);
      
      toast.success(`Betöltve: ${customersData.length} CRM ügyfél`);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Hiba az ügyfelek betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const loadBillingItems = async () => {
    if (!user?.organizationId) return;
    
    try {
      const q = query(
        collection(db, 'energiaProducts'),
        where('organizationId', '==', user.organizationId),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const itemsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BillingItem[];
      
      // Sort by sortOrder (ascending)
      const sortedItems = itemsData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      
      console.log('📋 Loaded billing items:', sortedItems.map(item => ({
        name: item.name,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
        sortOrder: item.sortOrder
      })));
      
      setBillingItems(sortedItems);
    } catch (error) {
      console.error('Error loading billing items:', error);
      toast.error('Hiba a számlázandó tételek betöltésekor');
    }
  };

  // Batch load consumption data for multiple PODs at once
  const loadConsumptionBatch = async (podIds: string[], year: number, month: number): Promise<Map<string, number>> => {
    const consumptionMap = new Map<string, number>();
    
    try {
      setLoadingProgress({ current: 0, total: podIds.length });
      
      // Process in batches of 10 to avoid overwhelming Firebase
      const batchSize = 10;
      for (let i = 0; i < podIds.length; i += batchSize) {
        const batch = podIds.slice(i, Math.min(i + batchSize, podIds.length));
        
        // Load batch in parallel
        const promises = batch.map(async (podId) => {
          // Check cache first - key includes month and data source
          const cacheKey = `${podId}__${year}-${String(month).padStart(2, '0')}__${dataSource}`;
          if (consumptionCache.has(cacheKey)) {
            return { podId, cacheKey, consumption: consumptionCache.get(cacheKey)! };
          }
          
          const consumption = await loadConsumptionForPod(podId, year, month);
          return { podId, cacheKey, consumption };
        });
        
        const results = await Promise.all(promises);
        results.forEach(({ podId, cacheKey, consumption }) => {
          consumptionMap.set(podId, consumption);
        });
        
        setLoadingProgress({ current: Math.min(i + batchSize, podIds.length), total: podIds.length });
      }
      
      // Update cache
      setConsumptionCache(prev => {
        const newCache = new Map(prev);
        consumptionMap.forEach((value, key) => {
          const cacheKey = `${key}__${year}-${String(month).padStart(2, '0')}__${dataSource}`;
          newCache.set(cacheKey, value);
        });
        return newCache;
      });
      
    } catch (error) {
      console.error('Error loading consumption batch:', error);
    } finally {
      setLoadingProgress({ current: 0, total: 0 });
    }
    
    return consumptionMap;
  };

  // Load consumption data for a POD for a specific period from energy_pods collection
  const loadConsumptionForPod = async (podId: string, year: number, month: number): Promise<number> => {
    try {
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;

      // --- MSCONS adatforrás ---
      if (dataSource === 'mscons') {
        const msConsRef = collection(db, POD_COLLECTION, podId, 'mscons');
        const msConsSnapshot = await getDocs(msConsRef);
        let consumption = 0;
        let reverseFeed = 0;
        msConsSnapshot.forEach((d) => {
          const data = d.data();
          // doc id or billing_period_end YYYYMM
          const periodEnd = data.billing_period_end ? String(data.billing_period_end) : '';
          const docMonthKey = periodEnd.length === 8 ? `${periodEnd.slice(0, 4)}-${periodEnd.slice(4, 6)}` : d.id.slice(0, 7);
          if (docMonthKey === monthKey) {
            consumption = parseFloat(data.total_consumption_kwh || 0);
            reverseFeed = parseFloat(data.total_reverse_feed_kwh || 0);
          }
        });
        if (consumption > 0) {
          let final = consumption;
          if (includeReverseFeed && reverseFeed > 0) {
            final = consumption - reverseFeed;
            if (skipZeroNetConsumption && final <= 0) return 0;
          }
          console.log(`📋 MSCONS ${podId} ${monthKey}: ${consumption} kWh (rf: ${reverseFeed})`);
          return Math.max(0, final);
        }
        console.log(`⚠️ No MSCONS data for ${podId} ${monthKey}`);
        return 0;
      }

      // --- Negyedórás mérések (daily_summaries) adatforrás ---
      // Csak az adott hónap napi dokumentumai (doc id = YYYYMMDD) – nem a POD teljes története
      const dailyRef = collection(db, POD_COLLECTION, podId, 'daily_summaries');
      const ym = `${year}${String(month).padStart(2, '0')}`;
      const dailySnapshot = await getDocs(query(dailyRef, where(documentId(), '>=', `${ym}01`), where(documentId(), '<=', `${ym}31`)));
      let consumption = 0;
      let reverseFeed = 0;
      dailySnapshot.forEach((d) => {
        const data = d.data();
        const date = data.date?.toDate ? data.date.toDate() : null;
        if (!date) return;
        const dMonthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (dMonthKey === monthKey) {
          consumption += parseFloat(data.total_consumption_kwh || 0);
          reverseFeed += parseFloat(data.total_reverse_feed_kwh || 0);
        }
      });
      if (consumption > 0) {
        let final = consumption;
        if (includeReverseFeed && reverseFeed > 0) {
          final = consumption - reverseFeed;
          if (skipZeroNetConsumption && final <= 0) return 0;
        }
        console.log(`📊 Mérések ${podId} ${monthKey}: ${consumption} kWh (rf: ${reverseFeed})`);
        return Math.max(0, final);
      }

      console.log(`⚠️ No consumption data for ${podId} ${monthKey}`);
      return 0;
    } catch (error) {
      console.error(`Error loading consumption for POD ${podId}:`, error);
      return 0;
    }
  };

  const handleCustomerSelection = (customerId: string) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAllCustomers = () => {
    const filtered = filteredCustomers;
    const allSelected = filtered.every(c => selectedCustomers.includes(c.id));
    
    if (allSelected) {
      setSelectedCustomers(prev => prev.filter(id => !filtered.find(c => c.id === id)));
    } else {
      setSelectedCustomers(prev => {
        const combined = [...prev, ...filtered.map(c => c.id)];
        return Array.from(new Set(combined));
      });
    }
  };

  const handleNextToItems = () => {
    if (selectedCustomers.length === 0) {
      toast.error('Válassz ki legalább egy ügyfelet!');
      return;
    }
    
    // Create invoice drafts for selected customers based on their invoicing mode
    const drafts: InvoiceDraft[] = [];
    
    selectedCustomers.forEach(customerId => {
      const customer = customers.find(c => c.id === customerId);
      if (!customer) return;
      
      const customerName = customer.companyName || customer.name || 'Ismeretlen';
      
      // Use customer's own invoicing mode setting, fallback to global setting
      const customerInvoicingMode = customer.bulkInvoicingSettings?.invoicingMode || invoicingMode;
      
      // Collect all PODs
      const allPods: POD[] = [];
      if (customer.pod) allPods.push(customer.pod);
      if (customer.additionalPods) allPods.push(...customer.additionalPods);
      
      if (allPods.length === 0) {
        // No PODs - create one draft for the customer
        const paymentDeadline = (customer as any).consumptionData?.dueDate 
          ? parseInt((customer as any).consumptionData.dueDate) 
          : 15;
        
        // Generate notes with POD info if available
        let notes = '';
        if (customer.pod?.id) {
          notes = `POD: ${customer.pod.id}`;
          if (customer.pod.address) {
            notes += `\nFogyasztási hely: ${customer.pod.address}`;
          }
        }
        
        drafts.push({
          id: `${customerId}`,
          customerId,
          customerName,
          selected: true,
          items: [],
          currency: customer.currentPricing?.currency || 'HUF',
          invoicingMode: customerInvoicingMode,
          performanceDate: new Date().toISOString().split('T')[0],
          paymentDeadline: paymentDeadline,
          notes: notes,
          invoiceType: 'proforma'
        });
      } else if (customerInvoicingMode === 'per_pod') {
        // Create separate invoice for each POD
        allPods.forEach((pod, index) => {
          if (pod.billable !== false) {
            const paymentDeadline = (customer as any).consumptionData?.dueDate 
              ? parseInt((customer as any).consumptionData.dueDate) 
              : 15;
            
            // Generate notes with POD info
            let notes = `POD: ${pod.id}`;
            if (pod.address) {
              notes += `\nFogyasztási hely: ${pod.address}`;
            }
            
            drafts.push({
              id: `${customerId}_pod_${index}`,
              customerId,
              customerName: `${customerName} - ${pod.id}`,
              podId: pod.id,
              podAddress: pod.address || [pod.postalCode, pod.city, pod.street].filter(Boolean).join(' '),
              selected: true,
              items: [],
              invoicingMode: 'per_pod',
              currency: customer.currentPricing?.currency || 'HUF',
              performanceDate: new Date().toISOString().split('T')[0],
              paymentDeadline: paymentDeadline,
              notes: notes,
              invoiceType: 'proforma'
            });
          }
        });
      } else if (customerInvoicingMode === 'all_pods_single_invoice') {
        // Create one invoice for all PODs
        const paymentDeadline = (customer as any).consumptionData?.dueDate 
          ? parseInt((customer as any).consumptionData.dueDate) 
          : 15;
        
        // Generate notes with all POD info
        let notes = `POD-ok (${allPods.length} db):\n`;
        allPods.forEach((pod, idx) => {
          notes += `${idx + 1}. ${pod.id}`;
          if (pod.address) {
            notes += ` - ${pod.address}`;
          }
          notes += '\n';
        });
        
        drafts.push({
          id: `${customerId}_all_pods`,
          customerId,
          customerName: `${customerName} (${allPods.length} POD)`,
          allPods: allPods.map(pod => ({ id: pod.id, address: pod.address, selected: true })),
          selected: true,
          items: [],
          invoicingMode: 'all_pods_single_invoice',
          currency: customer.currentPricing?.currency || 'HUF',
          performanceDate: new Date().toISOString().split('T')[0],
          paymentDeadline: paymentDeadline,
          notes: notes.trim(),
          invoiceType: 'proforma'
        });
      } else {
        // consolidated - group by invoiceGroupId
        const paymentDeadline = (customer as any).consumptionData?.dueDate 
          ? parseInt((customer as any).consumptionData.dueDate) 
          : 15;
        
        // Get invoice groups from customer
        const invoiceGroups = customer.invoiceGroups || [];
        
        if (invoiceGroups.length === 0) {
          // No groups defined, create one draft for all PODs
          let notes = `POD-ok (${allPods.length} db):\n`;
          allPods.forEach((pod, idx) => {
            notes += `${idx + 1}. ${pod.id}`;
            if (pod.address) {
              notes += ` - ${pod.address}`;
            }
            notes += '\n';
          });
          
          drafts.push({
            id: `${customerId}_consolidated`,
            customerId,
            customerName: `${customerName} (Konszolidált - ${allPods.length} POD)`,
            allPods: allPods.map(pod => ({ id: pod.id, address: pod.address, selected: true })),
            selected: true,
            items: [],
            invoicingMode: 'consolidated',
            currency: customer.currentPricing?.currency || 'HUF',
            performanceDate: new Date().toISOString().split('T')[0],
            paymentDeadline: paymentDeadline,
            notes: notes.trim(),
            invoiceType: 'proforma'
          });
        } else {
          // Create a draft for each invoice group
          invoiceGroups.forEach(group => {
            const groupPods = allPods.filter(pod => group.podIds.includes(pod.id));
            
            if (groupPods.length > 0) {
              let notes = `Gyűjtőszámla: ${group.name}\nPOD-ok (${groupPods.length} db):\n`;
              groupPods.forEach((pod, idx) => {
                notes += `${idx + 1}. ${pod.id}`;
                if (pod.address) {
                  notes += ` - ${pod.address}`;
                }
                notes += '\n';
              });
              
              drafts.push({
                id: `${customerId}_group_${group.id}`,
                customerId,
                customerName: `${customerName} - ${group.name} (${groupPods.length} POD)`,
                allPods: groupPods.map(pod => ({ id: pod.id, address: pod.address, selected: true })),
                selected: true,
                items: [],
                invoicingMode: 'consolidated',
                currency: customer.currentPricing?.currency || 'HUF',
                performanceDate: new Date().toISOString().split('T')[0],
                paymentDeadline: paymentDeadline,
                notes: notes.trim(),
                invoiceType: 'proforma'
              });
            }
          });
          
          // Add ungrouped PODs if any
          const groupedPodIds = new Set(invoiceGroups.flatMap(g => g.podIds));
          const ungroupedPods = allPods.filter(pod => !groupedPodIds.has(pod.id));
          
          if (ungroupedPods.length > 0) {
            let notes = `Csoportosítatlan POD-ok (${ungroupedPods.length} db):\n`;
            ungroupedPods.forEach((pod, idx) => {
              notes += `${idx + 1}. ${pod.id}`;
              if (pod.address) {
                notes += ` - ${pod.address}`;
              }
              notes += '\n';
            });
            
            drafts.push({
              id: `${customerId}_ungrouped`,
              customerId,
              customerName: `${customerName} - Csoportosítatlan (${ungroupedPods.length} POD)`,
              allPods: ungroupedPods.map(pod => ({ id: pod.id, address: pod.address, selected: false })),
              selected: true,
              items: [],
              invoicingMode: 'consolidated',
              currency: customer.currentPricing?.currency || 'HUF',
              performanceDate: new Date().toISOString().split('T')[0],
              paymentDeadline: paymentDeadline,
              notes: notes.trim(),
              invoiceType: 'proforma'
            });
          }
        }
      }
    });
    
    setInvoiceDrafts(drafts);
    setStep('items');
  };

  const handleAddItemToDraft = async (draftId: string, billingItem: BillingItem) => {
    const draft = invoiceDraftsRef.current.find(d => d.id === draftId);
    if (!draft) return;
    
    const customer = customers.find(c => c.id === draft.customerId);
    let customerPrice = getCustomerPrice(customer);
    let indexedPriceResult: IndexedPriceResult | null = null;
    
    // 🔄 Indexált ár számítása (ha indexált árazású szerződés)
    if (customer?.contractPeriod?.indexedPrice === true) {
      const targetPodId = draft.podId || draft.allPods?.find(p => p.selected !== false)?.id;
      if (targetPodId) {
        const paymentCurrency = customer.contractPeriod.paymentCurrency || 'HUF';
        const spread = customer.contractPeriod.spread;
        try {
          indexedPriceResult = await calculateIndexedUnitPrice(
            targetPodId,
            billingPeriod.year,
            billingPeriod.month,
            paymentCurrency,
            spread
          );
          if (indexedPriceResult) {
            customerPrice = indexedPriceResult.unitPrice;
          }
        } catch (error: any) {
          console.error(`⚠️ Indexált ár számítási hiba egyedi tételnél:`, error);
          toast.error(`Indexált ár számítása sikertelen: ${error.message || 'Ismeretlen hiba'}. Fix ár használva.`);
        }
      }
    }
    
    setInvoiceDrafts(prev => prev.map(draft => {
      if (draft.id === draftId) {
        const isEnergyItem = billingItem.name.toLowerCase().includes('energia') || 
                            billingItem.name.toLowerCase().includes('villamos') ||
                            billingItem.name.toLowerCase().includes('energiadíj');
        const finalPrice = isEnergyItem && customerPrice !== null ? customerPrice : billingItem.unitPrice;
        
        const newItem: InvoiceLineItem = {
          id: `${billingItem.id}-${Date.now()}`,
          billingItemId: billingItem.id,
          name: billingItem.name,
          quantity: 1,
          unit: billingItem.unit,
          unitPrice: finalPrice,
          taxRate: billingItem.taxRate,
          sortOrder: billingItem.sortOrder || 0,
          indexedPriceInfo: isEnergyItem && indexedPriceResult ? {
            isIndexed: true,
            hupxAvgPriceEUR: indexedPriceResult.totalCost / (indexedPriceResult.totalConsumption / 1000),
            eurHufRate: indexedPriceResult.eurHufRate,
            spread: indexedPriceResult.spread,
            totalConsumption: indexedPriceResult.totalConsumption,
            matchedIntervals: indexedPriceResult.matchedIntervals,
            currency: indexedPriceResult.currency
          } : undefined
        };
        
        return {
          ...draft,
          items: [...draft.items, newItem]
        };
      }
      return draft;
    }));
  };

  const handleAddAllItemsToDraft = async (draftId: string, consumptionData?: Map<string, number>) => {
    const draft = invoiceDraftsRef.current.find(d => d.id === draftId);
    if (!draft) return;
    
    // Find customer to get their pricing
    const customer = customers.find(c => c.id === draft.customerId);
    let customerPrice = getCustomerPrice(customer);
    let indexedPriceResult: IndexedPriceResult | null = null;
    
    // 🔄 Indexált ár számítása (ha indexált árazású szerződés)
    if (customer?.contractPeriod?.indexedPrice === true) {
      const targetPodId = draft.podId || draft.allPods?.find(p => p.selected !== false)?.id;
      if (targetPodId) {
        const paymentCurrency = customer.contractPeriod.paymentCurrency || 'HUF';
        const spread = customer.contractPeriod.spread;
        console.log(`🔢 Indexált ár számítása - POD: ${targetPodId}, Hónap: ${billingPeriod.year}-${String(billingPeriod.month).padStart(2, '0')}, Pénznem: ${paymentCurrency}, Spread: ${spread || 0}`);
        
        try {
          indexedPriceResult = await calculateIndexedUnitPrice(
            targetPodId,
            billingPeriod.year,
            billingPeriod.month,
            paymentCurrency,
            spread
          );
          
          if (indexedPriceResult) {
            customerPrice = indexedPriceResult.unitPrice;
            console.log(`✅ Indexált egységár: ${customerPrice.toFixed(4)} ${paymentCurrency}/kWh (HUPX: ${indexedPriceResult.matchedIntervals} időszelet, Fogyasztás: ${indexedPriceResult.totalConsumption.toFixed(2)} kWh)`);
            toast.success(`Indexált ár kiszámolva: ${customerPrice.toFixed(4)} ${paymentCurrency}/kWh`);
          }
        } catch (error: any) {
          console.error(`⚠️ Indexált ár számítási hiba:`, error);
          toast.error(`Indexált ár számítása sikertelen: ${error.message || 'Ismeretlen hiba'}. Fix ár használva.`);
        }
      } else {
        console.warn(`⚠️ Nincs POD ID az indexált ár számításához`);
        toast.error(`Nincs POD ID az indexált ár számításához`);
      }
    }
    
    console.log('🔍 Customer pricing:', {
      customerId: draft.customerId,
      customerName: customer?.companyName || customer?.name,
      customerPrice: customerPrice,
      currentPricing: customer?.currentPricing,
      offerPrice: customer?.offerPrice,
      contractPeriod: customer?.contractPeriod
    });
    
    console.log('📋 Billing items:', billingItems.map(item => ({
      name: item.name,
      unitPrice: item.unitPrice,
      sortOrder: item.sortOrder
    })));
    
    // If all_pods_single_invoice mode or consolidated mode, aggregate items by type
    if ((draft.invoicingMode === 'all_pods_single_invoice' || draft.invoicingMode === 'consolidated') && draft.allPods && draft.allPods.length > 0) {
      // Only process selected PODs
      const selectedPods = draft.allPods.filter(pod => pod.selected !== false);
      
      // Check if consumption is loaded for selected PODs
      const makeCacheKey = (podId: string) => `${podId}__${billingPeriod.year}-${String(billingPeriod.month).padStart(2, '0')}__${dataSource}`;
      const missingConsumption = selectedPods.filter(pod => !consumptionCache.has(makeCacheKey(pod.id)));
      if (missingConsumption.length > 0) {
        toast.error(`⚠️ Előbb töltsd be a fogyasztást a "📈 Összes fogyasztás betöltése" gombbal!`);
        console.warn(`Missing consumption for ${missingConsumption.length} PODs:`, missingConsumption.map(p => p.id));
      }
      
      // Aggregate items by billing item type
      const aggregatedItems = new Map<string, {
        billingItemId: string;
        name: string;
        totalQuantity: number;
        unit: string;
        unitPrice: number;
        taxRate: number;
        sortOrder: number;
        podDetails: string[];
      }>();

      // Load consumption for each selected POD and aggregate
      for (const pod of selectedPods) {
        let consumption = 0;
        
        // Use consumptionData parameter if provided (from batch load)
        if (consumptionData && consumptionData.has(pod.id)) {
          consumption = consumptionData.get(pod.id)!;
          console.log(`✅ Using consumption from batch data for ${pod.id}: ${consumption} kWh`);
        } else if (consumptionCache.has(makeCacheKey(pod.id))) {
          consumption = consumptionCache.get(makeCacheKey(pod.id))!;
          console.log(`✅ Using consumption from cache for ${pod.id}: ${consumption} kWh`);
        } else {
          consumption = await loadConsumptionForPod(pod.id, billingPeriod.year, billingPeriod.month);
          setConsumptionCache(prev => new Map(prev).set(makeCacheKey(pod.id), consumption));
          console.log(`✅ Loaded consumption for ${pod.id}: ${consumption} kWh`);
        }
        
        // Warn if no consumption data
        if (consumption === 0) {
          console.warn(`⚠️ No consumption data for POD ${pod.id} - using quantity 1`);
        }
        
        // Aggregate items for this POD
        billingItems.forEach((billingItem) => {
          const isEnergyItem = billingItem.name.toLowerCase().includes('energia') || 
                              billingItem.name.toLowerCase().includes('villamos') ||
                              billingItem.name.toLowerCase().includes('energiadíj');
          const finalPrice = isEnergyItem && customerPrice !== null ? customerPrice : billingItem.unitPrice;
          
          const key = `${billingItem.id}-${finalPrice}`;
          
          if (!aggregatedItems.has(key)) {
            aggregatedItems.set(key, {
              billingItemId: billingItem.id,
              name: billingItem.name,
              totalQuantity: 0,
              unit: billingItem.unit,
              unitPrice: finalPrice,
              taxRate: billingItem.taxRate,
              sortOrder: billingItem.sortOrder || 0,
              podDetails: []
            });
          }
          
          const aggItem = aggregatedItems.get(key)!;
          aggItem.totalQuantity += (consumption > 0 ? consumption : 1);
          
          // Store POD details for reference
          const podInfo = pod.address ? `${pod.id} (${pod.address})` : pod.id;
          aggItem.podDetails.push(`${podInfo}: ${consumption > 0 ? consumption.toFixed(2) : '1'} kWh`);
        });
      }

      // Convert aggregated items to InvoiceLineItem array
      const allNewItems: InvoiceLineItem[] = Array.from(aggregatedItems.values())
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item, index) => ({
          id: `${item.billingItemId}-aggregated-${Date.now()}-${index}`,
          billingItemId: item.billingItemId,
          name: item.name,
          quantity: item.totalQuantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          comment: `POD részletek:\n${item.podDetails.join('\n')}`,
          sortOrder: item.sortOrder,
          indexedPriceInfo: indexedPriceResult ? {
            isIndexed: true,
            hupxAvgPriceEUR: indexedPriceResult.totalCost / (indexedPriceResult.totalConsumption / 1000),
            eurHufRate: indexedPriceResult.eurHufRate,
            spread: indexedPriceResult.spread,
            totalConsumption: indexedPriceResult.totalConsumption,
            matchedIntervals: indexedPriceResult.matchedIntervals,
            currency: indexedPriceResult.currency
          } : undefined
        }));
      
      setInvoiceDrafts(prev => prev.map(d => {
        if (d.id === draftId) {
          return {
            ...d,
            items: allNewItems
          };
        }
        return d;
      }));
      
      console.log(`✅ ${allNewItems.length} aggregált tétel hozzáadva (${selectedPods.length} POD alapján)`);
      toast.success(`${allNewItems.length} aggregált tétel hozzáadva`);
      return;
    }
    
    // Original logic for single POD
    let consumption = 0;
    if (draft.podId) {
      // Check cache first
      const podCacheKey = `${draft.podId}__${billingPeriod.year}-${String(billingPeriod.month).padStart(2, '0')}__${dataSource}`;
      if (consumptionCache.has(podCacheKey)) {
        consumption = consumptionCache.get(podCacheKey)!;
      } else {
        consumption = await loadConsumptionForPod(draft.podId, billingPeriod.year, billingPeriod.month);
        setConsumptionCache(prev => new Map(prev).set(podCacheKey, consumption));
      }
    }
    
    setInvoiceDrafts(prev => prev.map(d => {
      if (d.id === draftId) {
        const newItems: InvoiceLineItem[] = billingItems.map((billingItem, index) => {
          // Only use customer price for energy/electricity items, use billing item price for others (KÁT, premium, etc.)
          const isEnergyItem = billingItem.name.toLowerCase().includes('energia') || 
                              billingItem.name.toLowerCase().includes('villamos') ||
                              billingItem.name.toLowerCase().includes('energiadíj');
          const finalPrice = isEnergyItem && customerPrice !== null ? customerPrice : billingItem.unitPrice;
          
          // Generate comment with POD info
          let comment = '';
          if (draft.podId) {
            comment = `POD: ${draft.podId}`;
            if (draft.podAddress) {
              comment += ` - ${draft.podAddress}`;
            }
          }
          
          return {
            id: `${billingItem.id}-${Date.now()}-${index}`,
            billingItemId: billingItem.id,
            name: billingItem.name,
            quantity: consumption > 0 ? consumption : 1, // Use consumption if available, otherwise 1
            unit: billingItem.unit,
            unitPrice: finalPrice,
            taxRate: billingItem.taxRate,
            comment: comment,
            sortOrder: billingItem.sortOrder || 0,
            indexedPriceInfo: isEnergyItem && indexedPriceResult ? {
              isIndexed: true,
              hupxAvgPriceEUR: indexedPriceResult.totalCost / (indexedPriceResult.totalConsumption / 1000),
              eurHufRate: indexedPriceResult.eurHufRate,
              spread: indexedPriceResult.spread,
              totalConsumption: indexedPriceResult.totalConsumption,
              matchedIntervals: indexedPriceResult.matchedIntervals,
              currency: indexedPriceResult.currency
            } : undefined
          };
        });
        
        return {
          ...d,
          items: newItems
        };
      }
      return d;
    }));
  };

  const handleUpdateItemQuantity = (draftId: string, itemId: string, quantity: number) => {
    setInvoiceDrafts(prev => prev.map(draft => {
      if (draft.id === draftId) {
        return {
          ...draft,
          items: draft.items.map(item => 
            item.id === itemId ? { ...item, quantity } : item
          )
        };
      }
      return draft;
    }));
  };

  const handleUpdateItemUnitPrice = (draftId: string, itemId: string, unitPrice: number) => {
    setInvoiceDrafts(prev => prev.map(draft => {
      if (draft.id === draftId) {
        return {
          ...draft,
          items: draft.items.map(item => 
            item.id === itemId ? { ...item, unitPrice } : item
          )
        };
      }
      return draft;
    }));
  };

  const handleDownloadPDF = (draftId: string) => {
    const draft = invoiceDrafts.find(d => d.id === draftId);
    if (!draft?.attachmentUrl) {
      toast.error('PDF melléklet nem elérhető. Először generálja a számlát.');
      return;
    }
    try {
      const link = document.createElement('a');
      link.href = draft.attachmentUrl;
      link.download = `${draft.attachmentCode || 'melleklet'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`PDF letöltve: ${draft.attachmentCode}`);
    } catch (error) {
      console.error('PDF letöltési hiba:', error);
      toast.error('Hiba a PDF letöltése közben');
    }
  };

  const handleDownloadExcel = (draftId: string) => {
    const draft = invoiceDrafts.find(d => d.id === draftId);
    if (!draft?.excelUrl) {
      toast.error('Excel melléklet nem elérhető. Először generálja a számlát.');
      return;
    }
    try {
      const link = document.createElement('a');
      link.href = draft.excelUrl;
      link.download = `${draft.attachmentCode || 'melleklet'}.xlsx`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Excel letöltve: ${draft.attachmentCode}`);
    } catch (error) {
      console.error('Excel letöltési hiba:', error);
      toast.error('Hiba az Excel letöltése közben');
    }
  };

  const handleRemoveItemFromDraft = (draftId: string, itemId: string) => {
    setInvoiceDrafts(prev => prev.map(draft => {
      if (draft.id === draftId) {
        return {
          ...draft,
          items: draft.items.filter(item => item.id !== itemId)
        };
      }
      return draft;
    }));
  };

  const handleGenerateInvoices = () => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }

    const selectedDrafts = invoiceDrafts.filter(d => d.selected && d.items.length > 0);
    
    if (selectedDrafts.length === 0) {
      toast.error('Nincs kiválasztott számla tételekkel');
      return;
    }

    // Show custom confirm modal
    setConfirmModal({
      isOpen: true,
      title: 'Számlák generálása',
      message: `Biztosan generálsz ${selectedDrafts.length} számlát?`,
      onConfirm: () => executeGenerateInvoices(selectedDrafts)
    });
  };

  const executeGenerateInvoices = async (selectedDrafts: InvoiceDraft[]) => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }
    // Close confirm modal
    setConfirmModal(prev => ({ ...prev, isOpen: false }));

    try {
      setGenerating(true);
      let success = 0;
      let failed = 0;
      
      // Initialize progress tracking
      const progressSteps = selectedDrafts.map(draft => ({
        customerName: draft.customerName,
        status: 'pending' as const,
        message: 'Várakozás...'
      }));
      
      setGenerationProgress({
        isOpen: true,
        total: selectedDrafts.length,
        current: 0,
        steps: progressSteps
      });
      
      // Collect invoice links per customer for grouped email sending
      const emailGroups: Record<string, {
        customerName: string;
        billingPeriod: string;
        invoices: Array<{ invoiceNumber: string; draftIndex: number }>;
        downloadToken: string;
      }> = {};

      for (let i = 0; i < selectedDrafts.length; i++) {
        const draft = selectedDrafts[i];
        
        // Update status to processing
        setGenerationProgress(prev => ({
          ...prev,
          current: i,
          steps: prev.steps.map((step, idx) => 
            idx === i ? { ...step, status: 'processing' as const, message: 'Számla generálása...' } : step
          )
        }));
        
        try {
          const customer = customers.find(c => c.id === draft.customerId);
          if (!customer) {
            setGenerationProgress(prev => ({
              ...prev,
              steps: prev.steps.map((step, idx) => 
                idx === i ? { ...step, status: 'error' as const, message: 'Ügyfél nem található' } : step
              )
            }));
            failed++;
            continue;
          }
          
          // Parse address from any format
          let city: string, postalCode: string, street: string;
          const isPerPodMode = draft.invoicingMode === 'per_pod';

          if (isPerPodMode && draft.podId) {
            // Find the actual POD object to get structured address fields
            const pod = customer.pod?.id === draft.podId ? customer.pod : customer.additionalPods?.find(p => p.id === draft.podId);
            if (pod) {
              city = pod.city || '';
              postalCode = pod.postalCode || '';
              street = pod.street || pod.address || '';
            } else {
              // Fallback to parsing the draft podAddress string
              const parsed = parseCustomerAddress({ address: draft.podAddress || '' });
              city = parsed.city || '';
              postalCode = parsed.postalCode || '';
              street = parsed.street || draft.podAddress || '';
            }
          } else {
            const parsed = parseCustomerAddress(customer);
            city = parsed.city;
            postalCode = parsed.postalCode;
            street = parsed.street;
          }
          const buyerName = customer.companyName || customer.name || 'Névtelen ügyfél';

          const hasAddress = isPerPodMode ? (street !== '') : (city && postalCode && street);

          if (!hasAddress) {
            failed++;
            setGenerationProgress(prev => ({
              ...prev,
              steps: prev.steps.map((step, idx) => 
                idx === i ? { ...step, status: 'error' as const, message: 'Hiányos címadatok' } : step
              )
            }));
            continue;
          }

          // Map invoice type to billingService format
          let invoiceType: 'INVOICE' | 'PROFORMA' | 'CREDITNOTE' = 'INVOICE';
          let isDraft = false;

          if (draft.invoiceType === 'draft') {
            invoiceType = 'INVOICE';
            isDraft = true;
          } else if (draft.invoiceType === 'proforma') {
            invoiceType = 'PROFORMA';
            isDraft = false;
          } else {
            invoiceType = 'INVOICE';
            isDraft = false;
          }

          console.log(`🔍 Invoice type for ${draft.customerName}:`, {
            draftInvoiceType: draft.invoiceType,
            mappedInvoiceType: invoiceType,
            isDraft
          });
          
          // Aggregate items by type (not per POD)
          const aggregatedItems = new Map<string, {
            name: string;
            totalQuantity: number;
            unit: string;
            unitPrice: number;
            taxRate: number;
            sortOrder: number;
            podDetails: Array<{ podId: string; address?: string; consumption: number; amount: number }>;
          }>();

          draft.items.forEach(item => {
            const key = `${item.billingItemId}-${item.unitPrice}`;
            if (!aggregatedItems.has(key)) {
              aggregatedItems.set(key, {
                name: item.name,
                totalQuantity: 0,
                unit: item.unit,
                unitPrice: item.unitPrice,
                taxRate: item.taxRate,
                sortOrder: item.sortOrder || 0,
                podDetails: []
              });
            }
            
            const aggItem = aggregatedItems.get(key)!;
            aggItem.totalQuantity += item.quantity;
            
            // Extract POD info from comment if exists
            if (item.comment) {
              const podMatch = item.comment.match(/POD: ([^\s-]+)/);
              const addressMatch = item.comment.match(/- (.+)$/);
              if (podMatch) {
                aggItem.podDetails.push({
                  podId: podMatch[1],
                  address: addressMatch ? addressMatch[1] : undefined,
                  consumption: item.quantity,
                  amount: item.quantity * item.unitPrice
                });
              }
            }
          });

          // Convert aggregated items to array and sort by sortOrder
          const finalItems = Array.from(aggregatedItems.values())
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(item => ({
              name: item.name,
              quantity: item.totalQuantity,
              unit: item.unit,
              unitPrice: item.unitPrice,
              taxRate: item.taxRate,
              sortOrder: item.sortOrder
            }));

          // Collect all POD details for attachment
          const allPodDetails: Array<{
            podId: string;
            address?: string;
            partnerId?: string;
            consumption: number;
            itemName: string;
            unitPrice: number;
            amount: number;
            taxRate?: number;
          }> = [];

          // Helper to get partnerId from customer by podId
          const getPodPartnerId = (pid: string): string | undefined => {
            if (customer.pod?.id === pid) return customer.pod?.partnerId;
            return customer.additionalPods?.find(p => p.id === pid)?.partnerId;
          };
          
          // Only include selected PODs
          const selectedPodIds = new Set(draft.allPods?.filter(p => p.selected !== false).map(p => p.id) || []);
          
          // First try to get from aggregated items (extracted from comment) - FILTER by selected
          aggregatedItems.forEach(item => {
            item.podDetails.forEach(pod => {
              if (selectedPodIds.has(pod.podId)) {
                allPodDetails.push({
                  podId: pod.podId,
                  address: pod.address,
                  partnerId: getPodPartnerId(pod.podId),
                  consumption: pod.consumption,
                  itemName: item.name,
                  unitPrice: item.unitPrice,
                  amount: pod.amount,
                  taxRate: item.taxRate
                });
              }
            });
          });
          
          // Fallback: Get POD details directly from draft - only selected PODs
          if (allPodDetails.length === 0) {
            if (draft.podId) {
              // Single POD mode - add every invoice item for this POD
              draft.items.forEach(item => {
                allPodDetails.push({
                  podId: draft.podId!,
                  address: draft.podAddress,
                  partnerId: getPodPartnerId(draft.podId!),
                  consumption: item.quantity,
                  itemName: item.name,
                  unitPrice: item.unitPrice,
                  amount: item.quantity * item.unitPrice,
                  taxRate: item.taxRate
                });
              });
            } else if (draft.allPods && draft.allPods.length > 0) {
              // Multiple PODs mode - distribute each item across selected PODs proportionally
              const selectedPods = draft.allPods.filter(p => p.selected !== false);
              
              // Get real consumption for each POD (cached)
              const podConsumptions: Record<string, number> = {};
              let totalRealConsumption = 0;
              for (const pod of selectedPods) {
                const consumption = await loadConsumptionForPod(pod.id, billingPeriod.year, billingPeriod.month);
                podConsumptions[pod.id] = consumption;
                totalRealConsumption += consumption;
              }
              
              draft.items.forEach(item => {
                selectedPods.forEach(pod => {
                  const podConsumption = podConsumptions[pod.id] || 0;
                  const podShare = totalRealConsumption > 0 ? podConsumption / totalRealConsumption : 0;
                  const podItemQuantity = item.quantity * podShare;
                  
                  allPodDetails.push({
                    podId: pod.id,
                    address: pod.address,
                    partnerId: getPodPartnerId(pod.id),
                    consumption: podItemQuantity,
                    itemName: item.name,
                    unitPrice: item.unitPrice,
                    amount: podItemQuantity * item.unitPrice,
                    taxRate: item.taxRate
                  });
                });
              });
            }
          }

          // Validate required fields before sending
          const validationErrors: string[] = [];
          if (!customer.companyName && !customer.name) validationErrors.push('ügyfél név');
          if (!isPerPodMode) {
            if (!city) validationErrors.push('település');
            if (!street) validationErrors.push('utca/házszám');
            if (!postalCode) validationErrors.push('irányítószám');
          } else {
            if (!street) validationErrors.push('POD cím');
          }
          // Collect all billing emails for this customer
          const allEmails = getAllCustomerEmails(customer);
          const customerEmail = allEmails.join(',');
          if (!customerEmail) validationErrors.push('email cím (kötelező e-számlához)');
          if (finalItems.length === 0) validationErrors.push('legalább egy számla tétel');
          finalItems.forEach((item, idx) => {
            if (!item.name?.trim()) validationErrors.push(`tétel #${idx + 1} megnevezés`);
            if (item.unitPrice == null || Number.isNaN(item.unitPrice)) validationErrors.push(`tétel #${idx + 1} egységár`);
          });

          if (validationErrors.length > 0) {
            failed++;
            setGenerationProgress(prev => ({
              ...prev,
              steps: prev.steps.map((step, idx) => 
                idx === i ? { ...step, status: 'error' as const, message: `Hiányzó adatok: ${validationErrors.join(', ')}` } : step
              )
            }));
            continue;
          }

          // Build invoice data
          const invoiceData: any = {
            buyer: {
              id: customer.id,
              name: buyerName,
              address: {
                city: city,
                postalCode: postalCode,
                street: street,
                country: 'Magyarország'
              },
              taxNumber: customer.taxNumber || '',
              email: customerEmail || ''
            },
            items: finalItems,
            paymentMethod: 'TRANSFER',
            paymentDeadline: draft.paymentDeadline || 15,
            currency: draft.currency || 'HUF',
            notes: draft.notes || '',
            performanceDate: draft.performanceDate || new Date().toISOString().split('T')[0],
            invoiceType: invoiceType,
            isDraft: isDraft
          };

          // Update progress: creating attachments
          setGenerationProgress(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => 
              idx === i ? { ...step, status: 'processing' as const, message: 'Mellékletek létrehozása...' } : step
            )
          }));

          // Find EUR/HUF rate from indexed price info (if any)
          const eurHufRate = draft.items.find(item => item.indexedPriceInfo?.isIndexed)?.indexedPriceInfo?.eurHufRate;

          // Single backend call: szamlazz.hu invoice + PDF + Excel attachments
          const result = await createInvoiceWithAttachments(
            invoiceData,
            user.organizationId,
            allPodDetails.length > 0 ? allPodDetails : undefined,
            billingPeriod,
            undefined,
            eurHufRate
          );

          console.log(`✅ Számla + mellékletek létrehozva: ${result.invoiceNumber}, kód: ${result.attachmentCode}`);

          // Save attachment URLs to draft for download
          setInvoiceDrafts(prev => prev.map(d => {
            if (d.id === draft.id) {
              return {
                ...d,
                attachmentCode: result.attachmentCode,
                attachmentUrl: result.pdfUrl || '',
                excelUrl: result.excelUrl || '',
              };
            }
            return d;
          }));

          // Collect invoice link for grouped email sending
          console.log(`📧 Email debug - draft ${i}: downloadToken="${result.downloadToken}", customerEmail="${customerEmail}"`);
          if (result.downloadToken && customerEmail) {
            const billingPeriodStr = billingPeriod ? 
              `${billingPeriod.year}. ${String(billingPeriod.month).padStart(2, '0')}.` : 
              '-';

            if (!emailGroups[customerEmail]) {
              emailGroups[customerEmail] = {
                customerName: draft.customerName,
                billingPeriod: billingPeriodStr,
                invoices: [],
                downloadToken: result.downloadToken
              };
            }
            emailGroups[customerEmail].downloadToken = result.downloadToken;
            emailGroups[customerEmail].invoices.push({
              invoiceNumber: result.invoiceNumber || 'Díjbekérő',
              draftIndex: i
            });
            console.log(`📧 Email debug - added to group for ${customerEmail}, invoices count: ${emailGroups[customerEmail].invoices.length}`);
          } else {
            console.warn(`📧 Email debug - SKIPPED draft ${i}: missing downloadToken or customerEmail`);
          }

          // Update progress: success with invoice details (email status updated after grouped send)
          const details = result.invoiceNumber ? `${result.invoiceNumber}` : 'Díjbekérő kész';
          setGenerationProgress(prev => ({
            ...prev,
            current: i + 1,
            steps: prev.steps.map((step, idx) => 
              idx === i ? { 
                ...step, 
                status: 'success' as const, 
                message: `${details}`,
                invoiceNumber: result.invoiceNumber,
                attachmentCode: result.attachmentCode,
              } : step
            )
          }));

          success++;
        } catch (error: any) {
          failed++;
          console.error(`Error creating invoice for ${draft.customerName}:`, error);
          setGenerationProgress(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => 
              idx === i ? { ...step, status: 'error' as const, message: error.message || 'Ismeretlen hiba' } : step
            )
          }));
        }
      }

      // Send grouped emails: one email per customer with all invoice links
      console.log('📧 Email debug - emailGroups keys:', Object.keys(emailGroups));
      if (Object.keys(emailGroups).length === 0) {
        console.warn('📧 Email debug - emailGroups is EMPTY, no emails will be sent');
      }
      
      for (const [email, group] of Object.entries(emailGroups)) {
        console.log(`📧 Email debug - sending to ${email} for ${group.customerName}, ${group.invoices.length} invoices`);
        try {
          const portalUrl = `${window.location.origin}/download/${group.downloadToken}`;
          const invoiceListHtml = group.invoices.map(inv => `
            <li style="margin-bottom: 4px; font-size: 14px;">${inv.invoiceNumber}</li>
          `).join('');

          const htmlMessage = `
            <p>Tisztelt <strong>${group.customerName}</strong>!</p>
            <p>Mellékelve küldjük aktuális számláit és mellékleteit.</p>
            <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 10px 14px; margin: 12px 0;">
              <p style="margin: 0; font-size: 14px;"><strong>Időszak:</strong> ${group.billingPeriod}</p>
              <p style="margin: 4px 0 0 0; font-size: 14px;"><strong>Számlák száma:</strong> ${group.invoices.length}</p>
            </div>
            <div style="text-align: center; margin: 24px 0;">
              <a href="${portalUrl}" style="display: inline-block; background: #10b981; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-size: 16px; font-weight: 600;">Ügyfél portál megnyitása</a>
            </div>
            <p style="font-size: 13px; color: #374151; margin: 12px 0 4px 0; font-weight: 500;">Számlák:</p>
            <ul style="margin: 0; padding-left: 18px; color: #4b5563;">
              ${invoiceListHtml}
            </ul>
            <p style="font-size: 12px; color: #6b7280; margin-top: 16px;">
              A link személyre szól és 30 napig érvényes.
            </p>
          `;

          const emailResult = await sendGeneralEmail({
            to: email,
            subject: `Számlák és mellékletek - ${group.customerName}`,
            title: 'Számla értesítés',
            recipient_name: group.customerName,
            message: htmlMessage
          });
          console.log(`📧 Email debug - sendGeneralEmail result:`, emailResult);

          // Send BCC copy to internal accounting email
          try {
            const bccResult = await sendGeneralEmail({
              to: 'szamla@aram-foldgaz.hu',
              subject: `[MÁSOLAT] Számlák és mellékletek - ${group.customerName}`,
              title: 'Számla értesítés (másolat)',
              recipient_name: group.customerName,
              message: htmlMessage + `<p style="font-size: 12px; color: #6b7280; margin-top: 16px;">Eredeti címzett: ${email}</p>`
            });
            console.log(`📧 Email debug - BCC sendGeneralEmail result:`, bccResult);
          } catch (bccError) {
            console.warn('BCC email küldési hiba:', bccError);
          }

          const groupEmailSent = emailResult.success;
          const groupEmailError = emailResult.success ? '' : (emailResult.message || 'Email hiba');

          // Update progress steps for all drafts in this group
          setGenerationProgress(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => {
              if (group.invoices.some(inv => inv.draftIndex === idx)) {
                return {
                  ...step,
                  message: `${step.message} | ${groupEmailSent ? 'Email elküldve' : `Email hiba: ${groupEmailError}`}`,
                  emailSent: groupEmailSent,
                  emailError: groupEmailError || undefined
                };
              }
              return step;
            })
          }));

          if (groupEmailSent) {
            console.log(`✅ Csoportos email elküldve: ${group.customerName} -> ${email.replace(/,/g, ', ')} (${group.invoices.length} számla)`);
          } else {
            console.warn(`⚠️ Csoportos email sikertelen: ${group.customerName} -> ${email}: ${groupEmailError}`);
          }
        } catch (emailError: any) {
          console.error('Csoportos email küldési hiba:', emailError);
        }
      }

      if (success > 0) {
        toast.success(`${success} számla sikeresen létrehozva! A mellékletek letölthetők alább.`);
      }

      if (failed > 0) {
        toast.error(`${failed} számla létrehozása sikertelen`);
      }
    } catch (error: any) {
      console.error('Error generating invoices:', error);
      toast.error(`Hiba a számlák generálása során: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.status === 'active' && (
      (c.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.taxNumber || '').includes(searchTerm)
    )
  );

  const calculateDraftTotal = (draft: InvoiceDraft) => {
    return draft.items.reduce((sum, item) => {
      const netTotal = item.quantity * item.unitPrice;
      const taxAmount = netTotal * (item.taxRate / 100);
      return sum + netTotal + taxAmount;
    }, 0);
  };

  return (
    <div className="h-full">
      <CommonPageHeader
        title="Tömeges Számlázás"
        subtitle="Számlák tömeges generálása"
        icon={ReceiptPercentIcon}
        showLastUpdate={false}
      />

      {/* Email Test Mode Toggle */}
      <div className="flex justify-end px-6 pt-2">
        <EmailTestModeToggle />
      </div>

      <div className="p-6">
        {/* Steps indicator */}
        <div className="mb-6 flex items-center justify-center space-x-4">
          <div className={`flex items-center ${step === 'customers' ? 'text-primary-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'customers' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
              1
            </div>
            <span className="ml-2 font-medium">Ügyfelek</span>
          </div>
          
          <div className="w-16 h-0.5 bg-gray-300"></div>
          
          <div className={`flex items-center ${step === 'items' ? 'text-primary-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'items' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="ml-2 font-medium">Tételek</span>
          </div>
          
          <div className="w-16 h-0.5 bg-gray-300"></div>
          
          <div className={`flex items-center ${step === 'review' ? 'text-primary-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'review' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
              3
            </div>
            <span className="ml-2 font-medium">Ellenőrzés</span>
          </div>
        </div>

        {/* Step 1: Customer Selection */}
        {step === 'customers' && (
          <div className="space-y-4">
            {/* 1. sor: Keresés + Számlázási időszak */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] max-w-md">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Keresés ügyfelek között..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Számlázási időszak:</label>
                <select
                  value={billingPeriod.year}
                  onChange={(e) => setBillingPeriod(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {[2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <select
                  value={billingPeriod.month}
                  onChange={(e) => setBillingPeriod(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      {new Date(2000, month - 1).toLocaleDateString('hu-HU', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 2. sor: Adatforrás + Számlázási mód + Gombok */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Adatforrás:</span>
                  <button
                    onClick={() => { setDataSource('measurements'); setConsumptionCache(new Map()); }}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      dataSource === 'measurements'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Negyedórás mérési adatok összesítéséből"
                  >
                    📊 Negyedórás
                  </button>
                  <button
                    onClick={() => { setDataSource('mscons'); setConsumptionCache(new Map()); }}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      dataSource === 'mscons'
                        ? 'bg-orange-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Szolgáltató által küldött MSCONS számlázási adatok"
                  >
                    📋 MSCONS
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Számlázási mód:</span>
                  <button
                    onClick={() => setInvoicingMode('all_pods_single_invoice')}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      invoicingMode === 'all_pods_single_invoice'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    📄 Egy számlára
                  </button>
                  <button
                    onClick={() => setInvoicingMode('per_pod')}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      invoicingMode === 'per_pod'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    📑 Külön számlákra
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleSelectAllCustomers}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {filteredCustomers.every(c => selectedCustomers.includes(c.id)) ? 'Kijelölés törlése' : 'Összes kijelölése'}
                </button>
                  
                <button
                  onClick={handleNextToItems}
                  disabled={selectedCustomers.length === 0}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Tovább ({selectedCustomers.length})
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-full text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                  <p className="mt-4 text-gray-600">Betöltés...</p>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
                  <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Még nincsenek ügyfelek</p>
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    onClick={() => handleCustomerSelection(customer.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCustomers.includes(customer.id)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{customer.companyName || customer.name || 'Névtelen ügyfél'}</h3>
                        {(() => {
                          const emails = getAllCustomerEmails(customer);
                          return emails.length > 0 ? (
                            <p className="text-sm text-gray-600 mt-1 truncate">
                              {emails.join(', ')}
                            </p>
                          ) : null;
                        })()}
                        {customer.taxNumber && (
                          <p className="text-sm text-gray-500 mt-1">Adószám: {customer.taxNumber}</p>
                        )}
                        {(customer.city || customer.street) && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {customer.city && `${customer.city}, `}
                            {customer.street || ''}
                          </p>
                        )}
                        
                        {/* POD információk */}
                        {(customer.pod || (customer.additionalPods && customer.additionalPods.length > 0)) && (() => {
                          const monthKey = `${billingPeriod.year}-${String(billingPeriod.month).padStart(2, '0')}`;
                          const hasPodData = (podId: string) => {
                            const dr = podDateRanges.get(podId);
                            if (!dr) return false;
                            const startKey = `${dr.start.getFullYear()}-${String(dr.start.getMonth() + 1).padStart(2, '0')}`;
                            const endKey = `${dr.end.getFullYear()}-${String(dr.end.getMonth() + 1).padStart(2, '0')}`;
                            return monthKey >= startKey && monthKey <= endKey;
                          };
                          const allPods = [customer.pod, ...(customer.additionalPods || [])].filter(Boolean) as POD[];
                          const podsWithData = allPods.filter(p => hasPodData(p.id)).length;
                          return (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-medium text-gray-700">POD-ok:</p>
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${podsWithData > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                  {podsWithData > 0 ? `✓ ${podsWithData}/${allPods.length} adat` : '✗ nincs adat'}
                                </span>
                              </div>
                              {customer.pod && (
                                <p className="text-xs text-gray-600 truncate">
                                  {hasPodData(customer.pod.id) ? '🟢' : '🔴'} {customer.pod.id}
                                </p>
                              )}
                              {customer.additionalPods && customer.additionalPods.slice(0, 2).map((pod, idx) => (
                                <p key={idx} className="text-xs text-gray-600 truncate">
                                  {hasPodData(pod.id) ? '🟢' : '🔴'} {pod.id}
                                </p>
                              ))}
                              {customer.additionalPods && customer.additionalPods.length > 2 && (
                                <p className="text-xs text-gray-500">+{customer.additionalPods.length - 2} további</p>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Árazási információk */}
                        {customer.currentPricing && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs font-medium text-primary-600">
                              Aktuális ár: {parseFloat(customer.currentPricing.price).toLocaleString()} {customer.currentPricing.currency}/kWh
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {selectedCustomers.includes(customer.id) && (
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Step 2: Add Items */}
        {step === 'items' && (
          <div className="space-y-6">
            {/* POD kiválasztó nézet all_pods_single_invoice és consolidated módban */}
            {invoiceDrafts.some(d => (d.invoicingMode === 'all_pods_single_invoice' || d.invoicingMode === 'consolidated') && d.allPods && d.allPods.length > 0) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">POD-ok kiválasztása</h3>
                {invoiceDrafts.filter(d => (d.invoicingMode === 'all_pods_single_invoice' || d.invoicingMode === 'consolidated')).map(draft => (
                  <div key={draft.id} className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{draft.customerName}</h4>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            const updated = invoiceDrafts.map(d => {
                              if (d.id === draft.id && d.allPods) {
                                return {
                                  ...d,
                                  allPods: d.allPods.map(pod => ({ ...pod, selected: true })),
                                  items: []
                                };
                              }
                              return d;
                            });
                            setInvoiceDrafts(updated);
                            invoiceDraftsRef.current = updated;
                            await handleAddAllItemsToDraft(draft.id);
                          }}
                          className="px-3 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg"
                        >
                          Összes kijelölése
                        </button>
                        <button
                          onClick={async () => {
                            const updated = invoiceDrafts.map(d => {
                              if (d.id === draft.id && d.allPods) {
                                return {
                                  ...d,
                                  allPods: d.allPods.map(pod => ({ ...pod, selected: false })),
                                  items: []
                                };
                              }
                              return d;
                            });
                            setInvoiceDrafts(updated);
                            invoiceDraftsRef.current = updated;
                            await handleAddAllItemsToDraft(draft.id);
                          }}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg"
                        >
                          Kijelölés törlése
                        </button>
                        <span className="text-sm text-gray-600">
                          {draft.allPods?.filter(p => p.selected !== false).length || 0} / {draft.allPods?.length || 0} kiválasztva
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto p-2">
                      {draft.allPods?.map((pod) => (
                        <div
                          key={pod.id}
                          onClick={async () => {
                            const updated = invoiceDrafts.map(d => {
                              if (d.id === draft.id && d.allPods) {
                                return {
                                  ...d,
                                  allPods: d.allPods.map(p => 
                                    p.id === pod.id ? { ...p, selected: !p.selected } : p
                                  ),
                                  items: []
                                };
                              }
                              return d;
                            });
                            setInvoiceDrafts(updated);
                            invoiceDraftsRef.current = updated;
                            await handleAddAllItemsToDraft(draft.id);
                          }}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            pod.selected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 bg-white hover:border-primary-300'
                          }`}
                        >
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              checked={pod.selected || false}
                              onChange={() => {}}
                              className="mt-1 mr-2 h-4 w-4 text-primary-600 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-mono text-gray-900 truncate">{pod.id}</p>
                              {pod.address && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {typeof pod.address === 'object'
                                    ? `${(pod.address as any).postalCode || ''} ${(pod.address as any).city || ''}, ${(pod.address as any).street || ''}`
                                    : pod.address}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Visszatáplálás számlázási opciók */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <span className="text-green-600">⚡</span>
                Visszatáplálás beállítások
              </h3>
              
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeReverseFeed}
                    onChange={(e) => setIncludeReverseFeed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-green-600 rounded focus:ring-green-500"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">Visszatáplálást számlázunk</span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      A fogyasztás és visszatáplálás szaldóval kerül számlázásra (Fogyasztás - Visszatáplálás)
                    </p>
                  </div>
                </label>
                
                {includeReverseFeed && (
                  <label className="flex items-start gap-3 cursor-pointer ml-7">
                    <input
                      type="checkbox"
                      checked={skipZeroNetConsumption}
                      onChange={(e) => setSkipZeroNetConsumption(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-yellow-600 rounded focus:ring-yellow-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">Nulla nettó fogyasztás kihagyása</span>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Ha a szaldó (Fogyasztás - Visszatáplálás) nulla vagy negatív, ne számlázza automatikusan
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep('customers')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                ← Vissza
              </button>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    // Collect only SELECTED POD IDs from both podId and allPods
                    const podIds: string[] = [];
                    
                    invoiceDrafts.forEach(draft => {
                      // Single POD mode
                      if (draft.podId) {
                        podIds.push(draft.podId);
                      }
                      // Multiple PODs mode (all_pods_single_invoice or consolidated)
                      // Only load consumption for SELECTED PODs
                      if (draft.allPods && draft.allPods.length > 0) {
                        draft.allPods.forEach(pod => {
                          if (pod.id && pod.selected !== false && !podIds.includes(pod.id)) {
                            podIds.push(pod.id);
                          }
                        });
                      }
                    });
                    
                    if (podIds.length > 0) {
                      toast.loading(`Fogyasztási adatok betöltése... (${podIds.length} POD)`);
                      const consumptionMap = await loadConsumptionBatch(podIds, billingPeriod.year, billingPeriod.month);
                      toast.dismiss();
                      
                      // Debug: Check what was loaded
                      console.log('📊 Consumption loaded:', Array.from(consumptionMap.entries()));
                      const loadedCount = Array.from(consumptionMap.values()).filter(v => v > 0).length;
                      
                      if (loadedCount === 0) {
                        toast.error(`⚠️ Nincs fogyasztási adat a kiválasztott POD-okhoz! Ellenőrizd a számlázási időszakot.`);
                      } else {
                        toast.success(`${loadedCount}/${podIds.length} POD fogyasztási adata betöltve!`);
                      }
                    } else {
                      toast.error('Nincs kiválasztott POD!');
                    }
                  }}
                  disabled={loadingProgress.total > 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingProgress.total > 0 ? (
                    `Betöltés... ${loadingProgress.current}/${loadingProgress.total}`
                  ) : (
                    (() => {
                      const totalPods = invoiceDrafts.reduce((count, d) => {
                        if (d.podId) count++;
                        if (d.allPods) {
                          // Only count SELECTED PODs
                          count += d.allPods.filter(p => p.selected !== false).length;
                        }
                        return count;
                      }, 0);
                      return `📈 Összes fogyasztás betöltése (${totalPods} kiválasztott POD)`;
                    })()
                  )}
                </button>
                
                <button
                  onClick={async () => {
                    // First, collect all selected POD IDs and load consumption
                    const podIds: string[] = [];
                    invoiceDrafts.forEach(draft => {
                      if (draft.podId) {
                        podIds.push(draft.podId);
                      }
                      if (draft.allPods && draft.allPods.length > 0) {
                        draft.allPods.forEach(pod => {
                          if (pod.id && pod.selected !== false && !podIds.includes(pod.id)) {
                            podIds.push(pod.id);
                          }
                        });
                      }
                    });
                    
                    // Load consumption for all PODs
                    let consumptionMap: Map<string, number> | undefined;
                    if (podIds.length > 0) {
                      toast.loading(`Fogyasztási adatok betöltése... (${podIds.length} POD)`);
                      consumptionMap = await loadConsumptionBatch(podIds, billingPeriod.year, billingPeriod.month);
                      toast.dismiss();
                    }
                    
                    // Add items to all drafts with consumption data
                    for (const draft of invoiceDrafts.filter(d => {
                      if (d.allPods && d.allPods.length > 0) {
                        return d.allPods.filter(p => p.selected !== false).length > 0;
                      }
                      return true;
                    })) {
                      await handleAddAllItemsToDraft(draft.id, consumptionMap);
                    }
                    
                    toast.success(`Tételek hozzáadva a számlákhoz!`);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  Összes tétel hozzáadása minden számlához
                </button>
                
                <button
                  onClick={() => setStep('review')}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Tovább az ellenőrzéshez
                </button>
              </div>
            </div>

            {invoiceDrafts.filter(draft => {
              // Filter out drafts with no selected PODs
              if (draft.allPods && draft.allPods.length > 0) {
                const selectedCount = draft.allPods.filter(p => p.selected !== false).length;
                return selectedCount > 0;
              }
              return true; // Keep single POD drafts
            }).map((draft) => (
              <div key={draft.id} className="bg-white rounded-lg shadow p-6">
                <div className="mb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{draft.customerName}</h3>
                      {draft.podId && (
                        <p className="text-sm text-gray-600 mt-1">POD: {draft.podId}</p>
                      )}
                      {draft.podId && (() => {
                        const c = customers.find(cu => cu.id === draft.customerId);
                        const p = c?.pod?.id === draft.podId ? c.pod : c?.additionalPods?.find(ap => ap.id === draft.podId);
                        if (!p) return null;
                        return (
                          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                            {p.city && p.postalCode && (
                              <p>{p.postalCode} {p.city}</p>
                            )}
                            {p.street && (
                              <p>{p.street}</p>
                            )}
                          </div>
                        );
                      })()}
                      {draft.invoicingMode && (
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-medium rounded bg-primary-100 text-primary-700">
                          {draft.invoicingMode === 'per_pod' && 'POD-onként'}
                          {draft.invoicingMode === 'all_pods_single_invoice' && 'Összes POD egy számlán'}
                          {draft.invoicingMode === 'consolidated' && 'Konszolidált'}
                        </span>
                      )}
                      
                      {/* 📊 Indexált ár indikátor */}
                      {(() => {
                        const c = customers.find(cu => cu.id === draft.customerId);
                        if (c?.contractPeriod?.indexedPrice) {
                          return (
                            <span className="inline-block mt-2 ml-2 px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                              📊 Indexált ár
                            </span>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* POD lista megjelenítése all_pods_single_invoice és consolidated módnál */}
                      {(draft.invoicingMode === 'all_pods_single_invoice' || draft.invoicingMode === 'consolidated') && draft.allPods && draft.allPods.length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-gray-700">
                              Kiválasztott POD-ok ({draft.allPods.filter(p => p.selected !== false).length} / {draft.allPods.length} db):
                            </p>
                            <button
                              onClick={() => {
                                const selectedPods = draft.allPods?.filter(p => p.selected !== false);
                                const podList = selectedPods?.map((pod, idx) => `${idx + 1}. ${pod.id}${pod.address ? ' - ' + pod.address : ''}`).join('\n');
                                navigator.clipboard.writeText(podList || '');
                                toast.success('POD lista vágólapra másolva!');
                              }}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            >
                              📋 Másolás
                            </button>
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {draft.allPods.filter(p => p.selected !== false).slice(0, 10).map((pod, idx) => (
                              <div key={pod.id} className="text-xs text-gray-600 flex items-start">
                                <span className="font-medium mr-2 text-green-600">✓</span>
                                <div className="flex-1">
                                  <p className="font-mono">{pod.id}</p>
                                  {pod.address && (
                                    <p className="text-gray-500">
                                      {typeof pod.address === 'object'
                                        ? `${(pod.address as any).postalCode || ''} ${(pod.address as any).city || ''}, ${(pod.address as any).street || ''}`
                                        : pod.address}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                            {draft.allPods.filter(p => p.selected !== false).length > 10 && (
                              <p className="text-xs text-gray-500 italic mt-2">
                                ... és még {draft.allPods.filter(p => p.selected !== false).length - 10} POD
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {draft.podId && (
                      <button
                        onClick={async () => {
                          const consumption = await loadConsumptionForPod(draft.podId!, billingPeriod.year, billingPeriod.month);
                          if (consumption > 0) {
                            toast.success(`Fogyasztás betöltve: ${consumption.toLocaleString()} kWh`);
                          } else {
                            toast.error(`Nincs fogyasztási adat erre a POD-ra: ${draft.podId}`);
                          }
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors flex items-center space-x-1"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                        <span>Fogyasztás betöltése</span>
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Items list */}
                <div className="space-y-2 mb-4">
                  {(draft.invoicingMode === 'all_pods_single_invoice' || draft.invoicingMode === 'consolidated') && draft.allPods && draft.allPods.length > 0 ? (
                    // Aggregált tételek megjelenítése
                    <div className="border border-gray-200 rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                        <div>
                          <h4 className="font-semibold text-gray-900">Aggregált számlázási tételek</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {draft.allPods.filter(p => p.selected !== false).length} POD összesítve
                          </p>
                          {draft.attachmentCode && (
                            <p className="text-xs text-green-600 mt-1 flex items-center">
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              PDF melléklet: {draft.attachmentCode}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-700">
                            Összesen: {draft.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice * (1 + item.taxRate / 100)), 0).toLocaleString()} Ft
                          </p>
                          <p className="text-xs text-gray-500">
                            {draft.items.length} tétel
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {draft.items.map((item) => (
                          <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <p className="text-xs text-gray-500">ÁFA: {item.taxRate}%</p>
                              
                              {/* 📊 Indexált ár részletek */}
                              {(() => {
                                const customer = customers.find(c => c.id === draft.customerId);
                                if (item.indexedPriceInfo?.isIndexed) {
                                  return (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                                      <p className="font-medium">📊 Indexált ár részletek:</p>
                                      <p>HUPX átlag: {item.indexedPriceInfo.hupxAvgPriceEUR.toFixed(2)} EUR/MWh</p>
                                      {item.indexedPriceInfo.eurHufRate && (
                                        <p>EUR/HUF árfolyam: {item.indexedPriceInfo.eurHufRate.toFixed(2)}</p>
                                      )}
                                      {item.indexedPriceInfo.spread ? (
                                        <p>Spread: {item.indexedPriceInfo.spread} {item.indexedPriceInfo.currency === 'HUF' ? 'Ft/kWh' : 'EUR/kWh'}</p>
                                      ) : (
                                        <p>Spread: nincs</p>
                                      )}
                                      <p>Időszeletek: {item.indexedPriceInfo.matchedIntervals} db</p>
                                      <p>Fogyasztás: {item.indexedPriceInfo.totalConsumption.toFixed(2)} kWh</p>
                                    </div>
                                  );
                                }
                                // Fallback: ha az ügyfél indexált áras de a tétel régi (nincs indexedPriceInfo)
                                if (customer?.contractPeriod?.indexedPrice) {
                                  return (
                                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                                      <p className="font-medium">📊 Indexált ár</p>
                                      <p>Töröld és add hozzá újra a tételeket a részletek megjelenítéséhez!</p>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              
                              {item.comment && (
                                <details className="mt-2">
                                  <summary className="text-xs text-primary-600 cursor-pointer hover:text-primary-700">
                                    📋 POD részletek ({item.comment.split('\n').length - 1} POD)
                                  </summary>
                                  <pre className="text-xs text-gray-600 mt-1 whitespace-pre-wrap bg-white p-2 rounded border border-gray-200">
                                    {item.comment}
                                  </pre>
                                </details>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <div className="flex flex-col">
                                <label className="text-xs text-gray-600 mb-1">Mennyiség</label>
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleUpdateItemQuantity(draft.id, item.id, parseFloat(e.target.value) || 0)}
                                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                              
                              <span className="text-sm text-gray-600 mt-5">{item.unit}</span>
                              
                              <div className="flex flex-col">
                                <label className="text-xs text-gray-600 mb-1">Egységár (Ft)</label>
                                <input
                                  type="number"
                                  value={item.unitPrice}
                                  onChange={(e) => handleUpdateItemUnitPrice(draft.id, item.id, parseFloat(e.target.value) || 0)}
                                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                              
                              <button
                                onClick={() => handleRemoveItemFromDraft(draft.id, item.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg mt-5"
                                title="Törlés"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    // Normál megjelenítés (per_pod mód)
                    draft.items.map((item) => (
                      <div key={item.id} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500">ÁFA: {item.taxRate}%</p>
                          
                          {/* 📊 Indexált ár részletek */}
                          {(() => {
                            const customer = customers.find(c => c.id === draft.customerId);
                            if (item.indexedPriceInfo?.isIndexed) {
                              return (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                                  <p className="font-medium">📊 Indexált ár részletek:</p>
                                  <p>HUPX átlag: {item.indexedPriceInfo.hupxAvgPriceEUR.toFixed(2)} EUR/MWh</p>
                                  {item.indexedPriceInfo.eurHufRate && (
                                    <p>EUR/HUF árfolyam: {item.indexedPriceInfo.eurHufRate.toFixed(2)}</p>
                                  )}
                                  {item.indexedPriceInfo.spread ? (
                                    <p>Spread: {item.indexedPriceInfo.spread} {item.indexedPriceInfo.currency === 'HUF' ? 'Ft/kWh' : 'EUR/kWh'}</p>
                                  ) : (
                                    <p>Spread: nincs</p>
                                  )}
                                  <p>Időszeletek: {item.indexedPriceInfo.matchedIntervals} db</p>
                                  <p>Fogyasztás: {item.indexedPriceInfo.totalConsumption.toFixed(2)} kWh</p>
                                </div>
                              );
                            }
                            if (customer?.contractPeriod?.indexedPrice) {
                              return (
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                                  <p className="font-medium">📊 Indexált ár</p>
                                  <p>Töröld és add hozzá újra a tételeket a részletek megjelenítéséhez!</p>
                                </div>
                              );
                            }
                            return null;
                          })()}
                          
                          {item.comment && (
                            <p className="text-xs text-gray-500 mt-1">💬 {item.comment}</p>
                          )}
                        </div>
                      
                      <div className="flex items-center space-x-2">
                        <div className="flex flex-col">
                          <label className="text-xs text-gray-600 mb-1">Mennyiség</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleUpdateItemQuantity(draft.id, item.id, parseFloat(e.target.value) || 0)}
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        
                        <span className="text-sm text-gray-600 mt-5">{item.unit}</span>
                        
                        <div className="flex flex-col">
                          <label className="text-xs text-gray-600 mb-1">Egységár (Ft)</label>
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => handleUpdateItemUnitPrice(draft.id, item.id, parseFloat(e.target.value) || 0)}
                            className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        
                        <button
                          onClick={() => handleRemoveItemFromDraft(draft.id, item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg mt-5"
                          title="Törlés"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))
                  )}
                </div>
                
                {/* Add item dropdown */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">Tétel hozzáadása:</p>
                    <button
                      onClick={() => handleAddAllItemsToDraft(draft.id)}
                      className="px-3 py-1 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                    >
                      Összes tétel hozzáadása
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {billingItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleAddItemToDraft(draft.id, item)}
                        className="p-2 text-left text-sm bg-gray-100 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
                      >
                        <div className="font-medium">{item.name}</div>
                        <div className="text-xs text-gray-600">{item.unitPrice.toLocaleString()} Ft/{item.unit}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Draft számlák:</strong> Válaszd ki, melyik számlát szeretnéd kiállítani. A nem kiválasztott számlák draft-ként maradnak és később is kiállíthatók.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep('items')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                ← Vissza
              </button>
              
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">
                  <strong>{invoiceDrafts.filter(d => d.selected && d.items.length > 0).length}</strong> / {invoiceDrafts.filter(d => d.items.length > 0).length} számla kiválasztva
                </div>
                
                <button
                  onClick={() => {
                    setInvoiceDrafts(prev => prev.map(d => ({ ...d, selected: d.items.length > 0 })));
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Összes kiválasztása
                </button>
                
                <button
                  onClick={() => {
                    setInvoiceDrafts(prev => prev.map(d => ({ ...d, selected: false })));
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Kijelölés törlése
                </button>
                
                <button
                  onClick={handleGenerateInvoices}
                  disabled={generating || invoiceDrafts.filter(d => d.selected && d.items.length > 0).length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {generating ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      <span>Generálás...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      <span>Számlák generálása ({invoiceDrafts.filter(d => d.selected && d.items.length > 0).length})</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {invoiceDrafts.filter(d => d.items.length > 0).map((draft) => (
                <div key={draft.id} className={`bg-white rounded-lg shadow p-6 border-2 transition-colors ${
                  draft.selected ? 'border-green-500 bg-green-50' : 'border-gray-200'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={draft.selected}
                        onChange={(e) => {
                          setInvoiceDrafts(prev => prev.map(d => 
                            d.id === draft.id ? { ...d, selected: e.target.checked } : d
                          ));
                        }}
                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{draft.customerName}</h3>
                        {(() => {
                          const customer = customers.find(c => c.id === draft.customerId);
                          if (customer?.contractPeriod?.indexedPrice) {
                            return (
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">
                                📊 Indexált ár
                              </span>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          const customer = customers.find(c => c.id === draft.customerId);
                          if (customer) {
                            const isPerPod = draft.invoicingMode === 'per_pod';
                            let city: string, postalCode: string, street: string;
                            if (isPerPod && draft.podAddress) {
                              const parsed = parseCustomerAddress({ address: draft.podAddress });
                              city = parsed.city || '';
                              postalCode = parsed.postalCode || '';
                              street = parsed.street || draft.podAddress;
                            } else {
                              const parsed = parseCustomerAddress(customer);
                              city = parsed.city;
                              postalCode = parsed.postalCode;
                              street = parsed.street;
                            }
                            const hasAddress = isPerPod ? (street !== '') : (city && postalCode && street);
                            const emails = getAllCustomerEmails(customer);
                            const emailList = emails.join(', ');
                            return (
                              <>
                                {customer.taxNumber && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    🏢 Adószám: {customer.taxNumber}
                                  </p>
                                )}
                                {hasAddress ? (
                                  <p className="text-xs text-gray-500 mt-1">
                                    📍 {postalCode} {city}, {street}
                                  </p>
                                ) : (
                                  <p className="text-xs text-red-600 mt-1 font-medium">
                                    ⚠️ Hiányzó címadatok: {isPerPod ? 'POD cím' : [!city && 'település', !postalCode && 'irányítószám', !street && 'utca/házszám'].filter(Boolean).join(', ')}
                                  </p>
                                )}
                                {emailList ? (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    📧 {emailList}
                                  </p>
                                ) : (
                                  <p className="text-xs text-red-600 mt-0.5 font-medium">
                                    ⚠️ Hiányzó email cím (kötelező e-számlához)
                                  </p>
                                )}
                              </>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setConfirmModal({
                          isOpen: true,
                          title: 'Draft törlése',
                          message: `Biztosan törölni szeretnéd ezt a draft számlát?\n${draft.customerName}`,
                          onConfirm: () => {
                            setInvoiceDrafts(prev => prev.filter(d => d.id !== draft.id));
                            setConfirmModal(prev => ({ ...prev, isOpen: false }));
                          }
                        });
                      }}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Draft törlése"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* Szerkeszthető számlázási beállítások */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Számlázási beállítások</h4>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Teljesítés dátuma</label>
                        <input
                          type="date"
                          value={draft.performanceDate || ''}
                          onChange={(e) => {
                            setInvoiceDrafts(prev => prev.map(d => 
                              d.id === draft.id ? { ...d, performanceDate: e.target.value } : d
                            ));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Fizetési határidő (nap)</label>
                        <input
                          type="number"
                          value={draft.paymentDeadline || 15}
                          onChange={(e) => {
                            setInvoiceDrafts(prev => prev.map(d => 
                              d.id === draft.id ? { ...d, paymentDeadline: parseInt(e.target.value) || 15 } : d
                            ));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                          min="1"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Devizanem</label>
                        <select
                          value={draft.currency || 'HUF'}
                          onChange={(e) => {
                            setInvoiceDrafts(prev => prev.map(d => 
                              d.id === draft.id ? { ...d, currency: e.target.value } : d
                            ));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="HUF">HUF (Forint)</option>
                          <option value="EUR">EUR (Euró)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Számla típus</label>
                        <select
                          value={draft.invoiceType || 'draft'}
                          onChange={(e) => {
                            setInvoiceDrafts(prev => prev.map(d => 
                              d.id === draft.id ? { ...d, invoiceType: e.target.value as 'draft' | 'proforma' | 'final' } : d
                            ));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="draft">📝 Draft (piszkozat)</option>
                          <option value="proforma">📄 Díjbekérő (proforma)</option>
                          <option value="final">✅ Éles számla</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Megjegyzés</label>
                      <textarea
                        value={draft.notes || ''}
                        onChange={(e) => {
                          setInvoiceDrafts(prev => prev.map(d => 
                            d.id === draft.id ? { ...d, notes: e.target.value } : d
                          ));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        rows={2}
                        placeholder="Opcionális megjegyzés a számlához..."
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {draft.items.map((item) => {
                      const netTotal = Math.round((item.quantity * item.unitPrice) * 100) / 100;
                      const taxAmount = Math.round((netTotal * (item.taxRate / 100)) * 100) / 100;
                      const grossTotal = Math.round((netTotal + taxAmount) * 100) / 100;
                      
                      return (
                        <div key={item.id}>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              {item.name} ({item.quantity.toFixed(2)} {item.unit} × {item.unitPrice.toLocaleString('hu-HU')} {draft.currency || 'Ft'})
                            </span>
                            <span className="font-medium">{grossTotal.toLocaleString('hu-HU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {draft.currency || 'Ft'}</span>
                          </div>
                          {item.indexedPriceInfo?.isIndexed && (
                            <div className="text-xs text-blue-600 mt-1 ml-4">
                              📊 HUPX: {item.indexedPriceInfo.hupxAvgPriceEUR.toFixed(2)} EUR/MWh
                              {item.indexedPriceInfo.eurHufRate && ` | Árfolyam: ${item.indexedPriceInfo.eurHufRate.toFixed(2)}`}
                              {item.indexedPriceInfo.spread ? ` | Spread: ${item.indexedPriceInfo.spread}` : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Összesen:</span>
                      <span className="text-primary-600">{calculateDraftTotal(draft).toLocaleString()} {draft.currency || 'Ft'}</span>
                    </div>
                  </div>

                  <div className="border-t mt-4 pt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleDownloadPDF(draft.id)}
                      disabled={!draft.attachmentUrl}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                      PDF letöltése
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownloadExcel(draft.id)}
                      disabled={!draft.excelUrl}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <TableCellsIcon className="h-4 w-4 mr-1.5" />
                      Excel letöltése
                    </button>
                    {draft.attachmentCode && (
                      <span className="text-xs text-gray-500">
                        {draft.attachmentCode}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Custom Confirm Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{confirmModal.title}</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600">{confirmModal.message}</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Mégse
              </button>
              <button
                onClick={() => confirmModal.onConfirm?.()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {confirmModal.title === 'Draft törlése' ? 'Törlés' : 'Generálás'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generation Progress Modal - Modern Design */}
      {generationProgress.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-8 py-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-green-50 to-white">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Számlázási folyamat
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {generationProgress.current} / {generationProgress.total} ügyfél feldolgozva
                </p>
              </div>
              <button
                onClick={() => setGenerationProgress(prev => ({ ...prev, isOpen: false }))}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                disabled={generating}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Progress bar with percentage */}
            <div className="px-8 py-4 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Folyamat</span>
                <span className="text-sm font-bold text-green-600">
                  {Math.round(generationProgress.total > 0 ? (generationProgress.current / generationProgress.total) * 100 : 0)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${generationProgress.total > 0 ? (generationProgress.current / generationProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Steps list - Timeline design */}
            <div className="px-8 py-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                {generationProgress.steps.map((step, idx) => (
                  <div 
                    key={idx}
                    className={`relative flex items-start p-4 rounded-xl border transition-all duration-300 ${
                      step.status === 'processing' ? 'bg-blue-50/80 border-blue-200 shadow-sm' :
                      step.status === 'success' ? 'bg-green-50/80 border-green-200' :
                      step.status === 'error' ? 'bg-red-50/80 border-red-200' :
                      'bg-gray-50 border-gray-200 opacity-70'
                    }`}
                  >
                    {/* Status icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                      step.status === 'pending' ? 'bg-gray-200' :
                      step.status === 'processing' ? 'bg-blue-500 animate-pulse' :
                      step.status === 'success' ? 'bg-green-500' :
                      'bg-red-500'
                    }`}>
                      {step.status === 'pending' && (
                        <span className="text-gray-500 text-sm font-bold">{idx + 1}</span>
                      )}
                      {step.status === 'processing' && (
                        <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                      )}
                      {step.status === 'success' && (
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {step.status === 'error' && (
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-gray-900 truncate">{step.customerName}</p>
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ml-2 flex-shrink-0 ${
                          step.status === 'pending' ? 'bg-gray-200 text-gray-600' :
                          step.status === 'processing' ? 'bg-blue-200 text-blue-700' :
                          step.status === 'success' ? 'bg-green-200 text-green-700' :
                          'bg-red-200 text-red-700'
                        }`}>
                          {step.status === 'pending' && 'Várakozik'}
                          {step.status === 'processing' && 'Folyamatban'}
                          {step.status === 'success' && 'Kész'}
                          {step.status === 'error' && 'Hiba'}
                        </span>
                      </div>
                      
                      <p className={`text-sm ${
                        step.status === 'error' ? 'text-red-600' :
                        step.status === 'success' ? 'text-green-700' :
                        'text-gray-500'
                      }`}>
                        {step.message}
                      </p>
                      
                      {/* Details row */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {step.invoiceNumber && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-md font-medium">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {step.invoiceNumber}
                          </span>
                        )}
                        {step.attachmentCode && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-md font-medium">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.707-1.414l-5.414-5.414A1 1 0 0012.586 2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            {step.attachmentCode}
                          </span>
                        )}
                        {step.emailSent && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-md font-medium">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Email elküldve
                          </span>
                        )}
                        {step.emailError && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-md font-medium">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Email hiba
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer - Summary */}
            <div className="px-8 py-5 border-t border-gray-100 bg-gray-50">
              {generating ? (
                <div className="flex items-center justify-center text-gray-600">
                  <svg className="animate-spin h-5 w-5 mr-3 text-green-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  <span className="font-medium">Feldolgozás folyamatban...</span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">
                        <span className="text-green-600 font-bold text-sm">
                          {generationProgress.steps.filter(s => s.status === 'success').length}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600">Sikeres</span>
                    </div>
                    {generationProgress.steps.filter(s => s.status === 'error').length > 0 && (
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                          <span className="text-red-600 font-bold text-sm">
                            {generationProgress.steps.filter(s => s.status === 'error').length}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600">Sikertelen</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                        <span className="text-blue-600 font-bold text-sm">
                          {generationProgress.steps.filter(s => s.emailSent).length}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600">Email elküldve</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setGenerationProgress(prev => ({ ...prev, isOpen: false }))}
                    className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors font-medium"
                  >
                    Bezárás
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkInvoiceManager;
